import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  AlertCircle,
  Clock3,
  Database,
  RefreshCw,
  Search,
} from "lucide-react";
import usePageTitle from "component/common/PageTitle";
import {
  fetchUpstoxOtmOptionPremium,
  refreshUpstoxOtmOptionPremium,
} from "service/api/UpstoxOtmOptionPremiumApi";
import useUpstoxOtmOptionPremiumWebsocket from "service/websocket/UpstoxOtmOptionPremiumWebsocket";
import "./StockLtpAnalysis.css";

const DEFAULT_ACCOUNT = "PK";
const ACCOUNT_OPTIONS = [DEFAULT_ACCOUNT];
const DEFAULT_ANALYSIS_VIEW = "index";

const numberValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getRowsFromPayload = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  return Array.isArray(payload?.data) ? payload.data : [];
};

const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const formatNumber = (value, fractionDigits = 2) => {
  const numericValue = numberValue(value);

  if (numericValue === null) {
    return "--";
  }

  return numericValue.toLocaleString("en-IN", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
};

const formatDateTime = (value) => (value ? String(value) : "--");

const formatDrawdown = (value) => {
  const numericValue = numberValue(value);

  return numericValue === null ? "" : `${numericValue.toFixed(2)}%`;
};

const getSymbol = (row) => row.underlyingSymbol || "--";

const getOptionType = (row) => String(row.optionType || "").toUpperCase();

const isIndexRow = (row) => {
  const underlyingType = String(row.underlyingType || "").toUpperCase();
  const underlyingKey = String(row.underlyingInstrumentKey || "").toUpperCase();

  return underlyingType === "INDEX" || underlyingKey.includes("_INDEX|");
};

const formatExpiryDate = (value) => {
  if (!value) {
    return "--";
  }

  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) {
    return String(value);
  }

  const expiryDate = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  const monthLabel = expiryDate
    .toLocaleString("en-US", { month: "short", timeZone: "UTC" })
    .toUpperCase();

  return `${day.padStart(2, "0")} ${monthLabel} ${year.slice(-2)}`;
};

const getHeaderExpiry = (rows) => {
  const expiryDates = [...new Set(rows.map((row) => row.expiryDate).filter(Boolean))];

  if (expiryDates.length === 0) {
    return "Waiting";
  }

  return expiryDates
    .sort()
    .slice(0, 2)
    .map(formatExpiryDate)
    .join(" · ");
};

const getBackendTimestamp = (rows) =>
  rows.find((row) => row.lastUpdatedDateTime)?.lastUpdatedDateTime || "";

const isHistoricalAnalysisComplete = (row) => {
  const status = String(row.historicalSyncStatus || "").toUpperCase();

  if (status === "COMPLETED") {
    return true;
  }

  // Older records may not have a sync status yet. Their persisted readiness
  // flags are the fallback so the header still reflects the saved backend
  // state immediately after a restart.
  return !status
    && row.allTimeExtremesInitialized === true
    && row.extremeSpotPricesInitialized === true
    && row.atlHistoryInitialized === true;
};

const getHistoryProgress = (rows) => {
  const total = rows.length;
  const completed = rows.filter(isHistoricalAnalysisComplete).length;

  return {
    total,
    completed,
    pending: Math.max(total - completed, 0),
  };
};

const getRowKey = (row, index) =>
  row.instrumentKey || `${getSymbol(row)}-${row.optionType}-${row.expiryDate}-${row.strikePrice}-${index}`;

const getSortValue = (row, sortBy) => {
  switch (sortBy) {
    case "strike":
      return numberValue(row.strikePrice) ?? Number.POSITIVE_INFINITY;
    case "spot":
      return numberValue(row.spotPrice) ?? Number.NEGATIVE_INFINITY;
    case "current":
      return numberValue(row.currentPremium) ?? Number.NEGATIVE_INFINITY;
    case "high":
      return numberValue(row.premiumHigh) ?? Number.NEGATIVE_INFINITY;
    case "low":
      return numberValue(row.premiumAtl2 ?? row.premiumLow) ?? Number.POSITIVE_INFINITY;
    case "rank":
      return numberValue(row.otmRank) ?? Number.POSITIVE_INFINITY;
    default:
      return `${getSymbol(row)}-${row.optionType || ""}-${row.otmRank || ""}`.toLowerCase();
  }
};

const getRowClassName = (row) => {
  const current = numberValue(row.currentPremium);
  const high = numberValue(row.premiumHigh);

  return current !== null && high !== null && current >= high ? "is-high" : "";
};

const isAtl1Reached = (row) => {
  const currentPremium = numberValue(row.currentPremium);
  const atl1 = numberValue(row.premiumAtl1);

  return currentPremium !== null && atl1 !== null && currentPremium >= atl1;
};

const getDateTimeSortValue = (value) => {
  const text = String(value || "").trim();
  if (!text) {
    return Number.NEGATIVE_INFINITY;
  }

  const indianDateTime = text.match(
    /^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (indianDateTime) {
    const [, day, month, year, hours = "00", minutes = "00", seconds = "00"] = indianDateTime;
    return Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours),
      Number(minutes),
      Number(seconds)
    );
  }

  const parsed = Date.parse(text.replace(" ", "T"));
  return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
};

const getLatestAtl1Row = (rows) => {
  const qualifyingRows = rows.filter(isAtl1Reached);

  return qualifyingRows.reduce((latest, row) => {
    if (!latest) {
      return row;
    }

    const rowTimestamps = [
      getDateTimeSortValue(row.lastUpdatedDateTime),
      getDateTimeSortValue(row.premiumAtl1DateTime),
      getDateTimeSortValue(row.premiumAtl2DateTime),
    ];
    const latestTimestamps = [
      getDateTimeSortValue(latest.lastUpdatedDateTime),
      getDateTimeSortValue(latest.premiumAtl1DateTime),
      getDateTimeSortValue(latest.premiumAtl2DateTime),
    ];

    for (let index = 0; index < rowTimestamps.length; index += 1) {
      if (rowTimestamps[index] !== latestTimestamps[index]) {
        return rowTimestamps[index] > latestTimestamps[index] ? row : latest;
      }
    }

    // Keep the last qualifying row as the deterministic tie-breaker when the
    // backend gives several rows the same update and ATL timestamps.
    return row;
  }, null);
};

function Metric({ value, tone = "neutral" }) {
  return <span className={`upstox-metric tone-${tone}`}>{value}</span>;
}

function PremiumWithDrawdown({ value, drawdownPercentage, tone, emphasized = false }) {
  const formattedDrawdown = formatDrawdown(drawdownPercentage);

  return (
    <span className="upstox-premium-inline">
      {emphasized ? (
        <strong className="upstox-ltp-value">{formatNumber(value)}</strong>
      ) : (
        <Metric value={formatNumber(value)} tone={tone} />
      )}
      {formattedDrawdown && (
        <strong className="upstox-drawdown-inline">({formattedDrawdown}) D</strong>
      )}
    </span>
  );
}

export default function StockLtpAnalysis() {
  usePageTitle();

  const pageRef = useRef(null);
  const tabsRef = useRef(null);
  const [accountName, setAccountName] = useState(DEFAULT_ACCOUNT);
  const [analysisView, setAnalysisView] = useState(DEFAULT_ANALYSIS_VIEW);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [optionTypeFilter, setOptionTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("symbol");
  const sortDirection = "asc";
  const [selectedRowKey, setSelectedRowKey] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [backendUpdatedAt, setBackendUpdatedAt] = useState("");
  const [error, setError] = useState("");
  const deferredAnalysisView = useDeferredValue(analysisView);

  const {
    rows: streamedRows,
    isConnected: isStreamConnected,
    error: streamError,
  } = useUpstoxOtmOptionPremiumWebsocket(accountName, autoRefresh);

  const requestInFlightRef = useRef(false);
  const abortControllerRef = useRef(null);

  const applyRows = useCallback((nextRows) => {
    setRows(nextRows);
    setBackendUpdatedAt(getBackendTimestamp(nextRows));
  }, []);

  const loadData = useCallback(
    async ({ silent = false } = {}) => {
      if (requestInFlightRef.current) {
        return;
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;
      requestInFlightRef.current = true;

      if (!silent) {
        setIsLoading(true);
        setIsRefreshing(true);
      }

      try {
        const response = await fetchUpstoxOtmOptionPremium(accountName, {
          signal: controller.signal,
        });
        applyRows(getRowsFromPayload(response.data));
        setError("");
      } catch (fetchError) {
        if (fetchError?.code === "ERR_CANCELED") {
          return;
        }

        setError(
          getApiErrorMessage(
            fetchError,
            "Unable to load OTM option premiums. Check the Upstox token."
          )
        );
      } finally {
        requestInFlightRef.current = false;
        abortControllerRef.current = null;

        if (!silent) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [accountName, applyRows]
  );

  useEffect(() => {
    setRows([]);
    setBackendUpdatedAt("");
    setError("");
    loadData();

    return () => abortControllerRef.current?.abort();
  }, [accountName, loadData]);

  useEffect(() => {
    if (!isStreamConnected || streamedRows.length === 0) {
      return;
    }

    applyRows(streamedRows);
    setError("");
  }, [applyRows, isStreamConnected, streamedRows]);

  useEffect(() => {
    if (streamError) {
      setError(streamError);
    }
  }, [streamError]);

  useEffect(() => {
    setSelectedRowKey("");
  }, [analysisView]);

  useEffect(() => {
    const page = pageRef.current;
    const analysisTabs = tabsRef.current;
    const controlBar = page?.querySelector(".upstox-control-bar");
    const appHeader = document.querySelector(".header.fixed-header");

    if (!page || !analysisTabs || !controlBar) {
      return undefined;
    }

    const updateStickyOffsets = () => {
      const headerIsFixed = appHeader
        && window.getComputedStyle(appHeader).position === "fixed";
      const headerHeight = headerIsFixed ? appHeader.getBoundingClientRect().height : 0;
      const tabsHeight = analysisTabs.getBoundingClientRect().height;
      const controlBarHeight = controlBar.getBoundingClientRect().height;

      page.style.setProperty("--upstox-app-header-height", `${headerHeight}px`);
      page.style.setProperty("--upstox-analysis-tabs-height", `${tabsHeight}px`);
      page.style.setProperty("--upstox-control-bar-height", `${controlBarHeight}px`);
    };

    updateStickyOffsets();
    window.addEventListener("resize", updateStickyOffsets);

    const resizeObserver = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(updateStickyOffsets);
    resizeObserver?.observe(analysisTabs);
    resizeObserver?.observe(controlBar);
    if (appHeader) {
      resizeObserver?.observe(appHeader);
    }

    return () => {
      window.removeEventListener("resize", updateStickyOffsets);
      resizeObserver?.disconnect();
    };
  }, []);

  const handleRefresh = async () => {
    if (isRefreshing) {
      return;
    }

    setIsRefreshing(true);
    try {
      const response = await refreshUpstoxOtmOptionPremium(accountName);
      applyRows(getRowsFromPayload(response.data));
      setError("");
    } catch (refreshError) {
      setError(
        getApiErrorMessage(
          refreshError,
          "OTM option premium refresh failed. Check the Upstox token and market status."
        )
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  const rowsByView = useMemo(() => {
    const indexRows = [];
    const stockRows = [];

    rows.forEach((row) => {
      if (isIndexRow(row)) {
        indexRows.push(row);
      } else {
        stockRows.push(row);
      }
    });

    return { indexRows, stockRows };
  }, [rows]);

  const activeRows = analysisView === "index" ? rowsByView.indexRows : rowsByView.stockRows;
  const renderedRows = deferredAnalysisView === "index"
    ? rowsByView.indexRows
    : rowsByView.stockRows;
  const isTableSwitching = analysisView !== deferredAnalysisView;
  const indexRowCount = rowsByView.indexRows.length;
  const stockRowCount = rowsByView.stockRows.length;

  const visibleRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return [...renderedRows]
      .filter((row) => {
        const searchableText = [
          row.underlyingSymbol,
          row.optionTradingSymbol,
          row.instrumentKey,
          row.optionType,
          row.strikePrice,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (term && !searchableText.includes(term)) {
          return false;
        }

        return optionTypeFilter === "all" || row.optionType === optionTypeFilter;
      })
      .sort((left, right) => {
        if (deferredAnalysisView === "index") {
          // Group each index completely before moving to the next index, then
          // keep that index's nearest expiry before its next expiry.
          const symbolOrder = getSymbol(left).localeCompare(getSymbol(right));
          if (symbolOrder !== 0) {
            return symbolOrder;
          }

          // Expiry dates are ISO values, so ascending order is chronological
          // order and must remain independent of the selected metric sort.
          const expiryOrder = String(left.expiryDate || "").localeCompare(
            String(right.expiryDate || "")
          );
          if (expiryOrder !== 0) {
            return expiryOrder;
          }
        }

        // Keep the requested visual grouping regardless of the selected
        // metric: index options first, stock options after them.
        const leftIsIndex = isIndexRow(left);
        const rightIsIndex = isIndexRow(right);
        if (leftIsIndex !== rightIsIndex) {
          return leftIsIndex ? -1 : 1;
        }

        const leftValue = getSortValue(left, sortBy);
        const rightValue = getSortValue(right, sortBy);
        const multiplier = sortDirection === "asc" ? 1 : -1;

        if (leftValue < rightValue) {
          return -1 * multiplier;
        }

        if (leftValue > rightValue) {
          return 1 * multiplier;
        }

        return 0;
      });
  }, [deferredAnalysisView, optionTypeFilter, renderedRows, searchTerm, sortBy, sortDirection]);

  const latestAtl1Row = useMemo(() => getLatestAtl1Row(visibleRows), [visibleRows]);

  const headerSlot =
    typeof document === "undefined"
      ? null
      : document.getElementById("strategy-header-slot");
  const headerExpiry = getHeaderExpiry(activeRows);
  const historyProgress = useMemo(() => getHistoryProgress(activeRows), [activeRows]);
  const activeViewLabel = analysisView === "index" ? "INDEX LTP" : "STOCK LTP";

  const handleAnalysisTabsClick = (event) => {
    const clickedButton = event.target instanceof Element
      ? event.target.closest("button[data-analysis-view]")
      : null;

    if (clickedButton) {
      setAnalysisView(clickedButton.dataset.analysisView);
      return;
    }

    const tabsBounds = event.currentTarget.getBoundingClientRect();
    const midpoint = tabsBounds.left + (tabsBounds.width / 2);
    setAnalysisView(event.clientX < midpoint ? "index" : "stock");
  };

  return (
    <>
      {headerSlot &&
        createPortal(
          <div className="upstox-header-status" aria-live="polite">
            <span className="upstox-header-status__brand">
              UPSTOX {activeViewLabel} · {accountName}
            </span>
            <span className="upstox-header-status__time">
              <Clock3 size={14} aria-hidden="true" />
              Backend update:
              <strong>{backendUpdatedAt || "Waiting for data"}</strong>
            </span>
            <span className="upstox-header-status__count">
              OTM count: <strong>{activeRows.length || "--"}</strong>
              {historyProgress.total > 0 && (
                <span className="upstox-header-status__progress" aria-label="Historical analysis progress">
                  <span aria-hidden="true">·</span>
                  <span>
                    Completed <strong>{historyProgress.completed}/{historyProgress.total}</strong>
                  </span>
                  <span>
                    Pending <strong>{historyProgress.pending}/{historyProgress.total}</strong>
                  </span>
                </span>
              )}
            </span>
            <span className="upstox-header-status__time">
              Expiry: <strong>{headerExpiry}</strong>
            </span>
            <span
              className={`upstox-header-status__mode${
                error ? " has-error" : !autoRefresh ? " is-paused" : ""
              }`}
            >
              {error
                ? "Needs attention"
                : autoRefresh
                  ? isStreamConnected
                    ? "LIVE · 10 SEC"
                    : "CONNECTING"
                  : "PAUSED"}
            </span>
            <label className="upstox-header-toggle">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(event) => setAutoRefresh(event.target.checked)}
              />
              <span className="upstox-header-toggle__track" aria-hidden="true">
                <span />
              </span>
              <span>Auto refresh</span>
            </label>
          </div>,
          headerSlot
        )}

      <main ref={pageRef} className="upstox-extreme-page">
        <div className="upstox-extreme-shell">
          <nav
            ref={tabsRef}
            className="upstox-analysis-tabs"
            aria-label="LTP analysis views"
            role="tablist"
            onClick={handleAnalysisTabsClick}
          >
            <button
              type="button"
              role="tab"
              data-analysis-view="index"
              aria-selected={analysisView === "index"}
              className={`upstox-analysis-tab${analysisView === "index" ? " is-active" : ""}`}
              onClick={() => setAnalysisView("index")}
            >
              INDEX LTP ANALYSIS
              <span>{indexRowCount}</span>
            </button>
            <button
              type="button"
              role="tab"
              data-analysis-view="stock"
              aria-selected={analysisView === "stock"}
              className={`upstox-analysis-tab${analysisView === "stock" ? " is-active" : ""}`}
              onClick={() => setAnalysisView("stock")}
            >
              STOCK LTP ANALYSIS
              <span>{stockRowCount}</span>
            </button>
          </nav>

          <section className="upstox-control-bar" aria-label="OTM option controls">
            <label className="upstox-control-item upstox-filter-field upstox-account-field">
              <span>Account</span>
              <select value={accountName} onChange={(event) => setAccountName(event.target.value)}>
                {ACCOUNT_OPTIONS.map((account) => (
                  <option key={account} value={account}>
                    {account}
                  </option>
                ))}
              </select>
            </label>

            <button
              type="button"
              className="upstox-button secondary upstox-toolbar-refresh"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw size={16} className={isRefreshing ? "spin" : ""} />
              Refresh now
            </button>

            <div className="upstox-control-item upstox-search-field">
              <Search size={17} aria-hidden="true" />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search stock, option or strike"
                aria-label="Search stock, option or strike"
              />
            </div>

            <label className="upstox-control-item upstox-filter-field">
              <span>Side</span>
              <select value={optionTypeFilter} onChange={(event) => setOptionTypeFilter(event.target.value)}>
                <option value="all">CE + PE</option>
                <option value="CE">Call · CE</option>
                <option value="PE">Put · PE</option>
              </select>
            </label>

            <label className="upstox-control-item upstox-filter-field">
              <span>Sort by</span>
              <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                <option value="symbol">Stock</option>
                <option value="rank">OTM rank</option>
                <option value="strike">Strike</option>
                <option value="spot">Spot</option>
                <option value="current">CE/PE LTP</option>
                <option value="high">ATH</option>
                <option value="low">ATL</option>
              </select>
            </label>

          </section>

          <section className="upstox-data-card">
            {error && (
              <div className="upstox-alert" role="alert">
                <AlertCircle size={17} />
                <span>{error}</span>
              </div>
            )}

            <div className="upstox-table-wrap">
              <table className="upstox-table" aria-busy={isTableSwitching}>
                <thead>
                  <tr>
                    <th scope="col">COUNT</th>
                    <th scope="col">SYMBOL</th>
                    <th scope="col">EXPIRY</th>
                    <th scope="col">Strike</th>
                    <th scope="col">SPOT</th>
                    <th scope="col">CE/PE LTP</th>
                    <th scope="col">ATL-1</th>
                    <th scope="col">ATL-1 spot</th>
                    <th scope="col">ATL-1 date</th>
                    <th scope="col">ATL-2</th>
                    <th scope="col">ATL-2 spot</th>
                    <th scope="col">ATL-2 date</th>
                    <th scope="col">ATH</th>
                    <th scope="col">LOT</th>
                    <th scope="col">Last update</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && rows.length === 0
                    ? Array.from({ length: 6 }, (_, index) => (
                        <tr className="upstox-skeleton-row" key={`skeleton-${index}`}>
                          {Array.from({ length: 15 }, (_, cellIndex) => (
                            <td key={`skeleton-cell-${cellIndex}`}>
                              <span className="upstox-skeleton" />
                            </td>
                          ))}
                        </tr>
                      ))
                    : visibleRows.map((row, index) => {
                        const rowKey = getRowKey(row, index);
                        const atl1Reached = isAtl1Reached(row);
                        const rowClassName = [
                          getRowClassName(row),
                          atl1Reached ? "is-atl1-reached" : "",
                          atl1Reached && row === latestAtl1Row ? "is-atl1-latest" : "",
                          selectedRowKey === rowKey ? "is-selected" : "",
                        ]
                          .filter(Boolean)
                          .join(" ");

                        return (
                        <tr
                          className={rowClassName}
                          key={rowKey}
                          tabIndex="0"
                          aria-selected={selectedRowKey === rowKey}
                          onClick={() => setSelectedRowKey(rowKey)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setSelectedRowKey(rowKey);
                            }
                          }}
                        >
                          <td data-label="COUNT">
                            <strong>#{row.otmRank || "--"}</strong>
                          </td>
                          <td data-label="SYMBOL">
                            <div className="upstox-symbol-cell">
                              <strong>{getSymbol(row)}</strong>
                            </div>
                          </td>
                          <td data-label="EXPIRY">
                            <span className="upstox-date-value">
                              {formatExpiryDate(row.expiryDate)}
                            </span>
                          </td>
                          <td data-label="Strike">
                            <strong>
                              {formatNumber(row.strikePrice)}{" "}
                              <span
                                className={`upstox-option-type ${
                                  getOptionType(row) === "CE"
                                    ? "is-ce"
                                    : getOptionType(row) === "PE"
                                      ? "is-pe"
                                      : ""
                                }`}
                              >
                                {getOptionType(row) || "--"}
                              </span>
                            </strong>
                          </td>
                          <td data-label="SPOT">
                            <strong className="upstox-ltp-value">{formatNumber(row.spotPrice)}</strong>
                          </td>
                          <td data-label="CE/PE LTP">
                            <PremiumWithDrawdown
                              value={row.currentPremium}
                              drawdownPercentage={row.currentPremiumDrawdownPercentage}
                              emphasized
                            />
                          </td>
                          <td data-label="ATL-1">
                            <PremiumWithDrawdown
                              value={row.premiumAtl1}
                              drawdownPercentage={row.premiumAtl1DrawdownPercentage}
                              tone="red"
                            />
                          </td>
                          <td data-label="ATL-1 spot">
                            <div className="upstox-high-low">
                              <span>{formatNumber(row.premiumAtl1SpotHigh)}</span>
                              <span>{formatNumber(row.premiumAtl1SpotLow)}</span>
                            </div>
                          </td>
                          <td data-label="ATL-1 date">
                            <span className="upstox-date-value">
                              {formatDateTime(row.premiumAtl1DateTime)}
                            </span>
                          </td>
                          <td data-label="ATL-2">
                            <PremiumWithDrawdown
                              value={row.premiumAtl2}
                              drawdownPercentage={row.premiumAtl2DrawdownPercentage}
                              tone="red"
                            />
                          </td>
                          <td data-label="ATL-2 spot">
                            <div className="upstox-high-low">
                              <span>{formatNumber(row.premiumAtl2SpotHigh)}</span>
                              <span>{formatNumber(row.premiumAtl2SpotLow)}</span>
                            </div>
                          </td>
                          <td data-label="ATL-2 date">
                            <span className="upstox-date-value">
                              {formatDateTime(row.premiumAtl2DateTime)}
                            </span>
                          </td>
                          <td data-label="ATH">
                            <Metric value={formatNumber(row.premiumHigh)} tone="green" />
                          </td>
                          <td data-label="LOT SIZE">
                            <strong>{formatNumber(row.lotSize, 0)}</strong>
                          </td>
                          <td data-label="Last update">
                            <span className="upstox-date-value">{formatDateTime(row.lastUpdatedDateTime)}</span>
                          </td>
                        </tr>
                        );
                      })}

                  {!isLoading && renderedRows.length === 0 && (
                    <tr>
                      <td colSpan="14" className="upstox-state-cell">
                        <Database size={25} />
                        <strong>No {deferredAnalysisView === "index" ? "index" : "stock"} option premium data yet</strong>
                        <span>Refresh the OTM premium data after the account token is available.</span>
                      </td>
                    </tr>
                  )}

                  {!isLoading && renderedRows.length > 0 && visibleRows.length === 0 && (
                    <tr>
                      <td colSpan="14" className="upstox-state-cell">
                        <Search size={25} />
                        <strong>No matching option contracts</strong>
                        <span>Try another stock, strike, or CE/PE filter.</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <footer className="upstox-data-card-footer">
              <span>
                <span className="upstox-status-dot live" />
                ATH/ATL levels use daily candles; ATL timing and spot ranges use 5-minute candles · IST · 24-hour format
              </span>
              <span>Index: two nearest expiries · Stock: nearest expiry · Backend scheduler: every 10 seconds · WebSocket updates</span>
            </footer>
          </section>
        </div>
      </main>
      <div className="upstox-page-footer">
        <div className="upstox-footer-left">
          <span>noptionwave © 2026</span>
        </div>
      </div>
    </>
  );
}
